import { z } from "zod";
import {
  parentAskStatusSchema,
  parentClaimRelationSchema,
  parentClaimSourceSchema,
  parentClaimStatusSchema,
  parentMatchConfidenceSchema,
  parentMatchSourceSchema,
  pendingLinkedViaSchema,
  pendingLinkKindSchema,
  pendingParticipantStatusSchema,
  staffRequestWhatsappStatusSchema,
} from "../enums.ts";
import { isoDateTimeSchema, uuidSchema } from "./common.ts";
import { familyInviteStateSchema } from "./families.ts";
import { sessionsLeftSchema } from "../sessions-left.ts";

/**
 * Coaches add their own swimmers (strategy repo: teamna/prototypes/coach-adds-swimmers.html,
 * round 2, agreed 2026-10-09). A coach types names under each level they teach. The owner links
 * each name to a parent, or makes the swimmer the holder of their own account, and picks the days.
 *
 * The words in code stay the system words: "participant" for a swimmer, "staff" for a coach,
 * "group" for a level. The screens read the business labels.
 */

const personNameSchema = z.string().trim().min(1).max(120);
const phoneInputSchema = z.string().trim().max(40);

/** One weekly time of a level, as the coach and the owner see it. */
export const levelWeeklyTimeSchema = z.object({
  templateId: uuidSchema,
  /** 0 is Sunday. */
  weekday: z.number().int().min(0).max(6),
  /** "18:00". */
  startTime: z.string(),
  staffUserId: uuidSchema.nullable(),
  staffName: z.string().nullable(),
});
export type LevelWeeklyTime = z.infer<typeof levelWeeklyTimeSchema>;

/** A name of the level that a coach checks a new name against: name and coaches, nothing about the family. */
export const levelNameSchema = z.object({
  /** A participant id, or a waiting name id. */
  id: uuidSchema,
  kind: z.enum(["participant", "waiting"]),
  name: z.string(),
  coachNames: z.array(z.string()),
  /** The viewer already has this swimmer in their list. */
  mine: z.boolean(),
});
export type LevelName = z.infer<typeof levelNameSchema>;

/** A name the coach typed that waits for the owner. The note is there only for the coach who typed it. */
export const staffWaitingSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  addedByMe: z.boolean(),
  /** Other coaches who say this swimmer is theirs too. */
  otherCoachNames: z.array(z.string()),
  noteParentName: z.string().nullable(),
  notePhoneE164: z.string().nullable(),
  /** Not told to the owner yet: the coach has not tapped Done. */
  inOpenBatch: z.boolean(),
  /** A claim on a swimmer who already has a parent: the owner only adds the coach's days. */
  claimOfParticipant: z.boolean(),
  createdAt: isoDateTimeSchema,
});
export type StaffWaiting = z.infer<typeof staffWaitingSchema>;

export const staffLevelParticipantSchema = z.object({
  participantId: uuidSchema,
  name: z.string(),
  /** The days of this swimmer, for example [1, 3]. Empty when the owner set none. */
  weekdays: z.array(z.number().int().min(0).max(6)),
});
export type StaffLevelParticipant = z.infer<typeof staffLevelParticipantSchema>;

export const staffLevelSchema = z.object({
  groupId: uuidSchema,
  name: z.string(),
  color: z.string().nullable(),
  /** The weekly times of this level where the viewer coaches. */
  myWeeklyTimes: z.array(levelWeeklyTimeSchema),
  participants: z.array(staffLevelParticipantSchema),
  waiting: z.array(staffWaitingSchema),
  /** Every name of the level, from every coach, for the similar-name check. */
  names: z.array(levelNameSchema),
});
export type StaffLevel = z.infer<typeof staffLevelSchema>;

/** `GET /business/businesses/:id/staff/participants`: the coach's own swimmers, level by level. */
export const staffParticipantsResponseSchema = z.object({
  /** The owner's switch. Off: the coach sees the list but cannot add. */
  canAdd: z.boolean(),
  /** The last time the owner asked this coach to add their swimmers. */
  askedAt: isoDateTimeSchema.nullable(),
  askedByName: z.string().nullable(),
  levels: z.array(staffLevelSchema),
  /** Names this coach added that the owner was not told about yet. */
  openBatch: z.number().int(),
});
export type StaffParticipantsResponse = z.infer<typeof staffParticipantsResponseSchema>;

/**
 * `POST .../staff/participants`: names under one level. A name like a swimmer of the level is held
 * back, unless `confirmedNew` says the coach checked and it is a new swimmer.
 */
export const addStaffParticipantsRequestSchema = z.object({
  groupId: uuidSchema,
  names: z.array(personNameSchema).min(1).max(200),
  confirmedNew: z.boolean().optional(),
  noteParentName: personNameSchema.nullable().optional(),
  notePhone: phoneInputSchema.nullable().optional(),
});
export type AddStaffParticipantsRequest = z.infer<typeof addStaffParticipantsRequestSchema>;

export const addStaffParticipantsResponseSchema = z.object({
  added: z.array(staffWaitingSchema),
  /** Names held back because the level has a similar name. */
  held: z.array(z.object({ name: z.string(), matches: z.array(levelNameSchema) })),
});
export type AddStaffParticipantsResponse = z.infer<typeof addStaffParticipantsResponseSchema>;

/** `POST .../staff/participants/same`: "Same swimmer". The coach says a name of the level is theirs too. */
export const claimStaffParticipantRequestSchema = z.object({
  kind: z.enum(["participant", "waiting"]),
  id: uuidSchema,
});
export type ClaimStaffParticipantRequest = z.infer<typeof claimStaffParticipantRequestSchema>;

/** `PATCH .../staff/participants/:id`: the coach fixes a name or a note while it waits. */
export const updateStaffParticipantRequestSchema = z.object({
  name: personNameSchema.optional(),
  noteParentName: z.string().trim().max(120).nullable().optional(),
  notePhone: phoneInputSchema.nullable().optional(),
});
export type UpdateStaffParticipantRequest = z.infer<typeof updateStaffParticipantRequestSchema>;

/** `POST .../staff/participants/done`: tell the owner about the open batch, in one message. */
export const staffParticipantsDoneResponseSchema = z.object({ told: z.number().int() });
export type StaffParticipantsDoneResponse = z.infer<typeof staffParticipantsDoneResponseSchema>;

// --- owner -------------------------------------------------------------------

/** A parent Teamna thinks a waiting name belongs to. `sure`: the coach wrote this parent's mobile. */
export const parentSuggestionSchema = z.object({
  familyId: uuidSchema,
  sure: z.boolean(),
  reason: z.enum(["mobile", "family_name"]),
});
export type ParentSuggestion = z.infer<typeof parentSuggestionSchema>;

export const ownerWaitingSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  status: pendingParticipantStatusSchema,
  groupId: uuidSchema,
  groupName: z.string(),
  groupColor: z.string().nullable(),
  coachNames: z.array(z.string()),
  addedByName: z.string().nullable(),
  noteParentName: z.string().nullable(),
  notePhoneE164: z.string().nullable(),
  createdAt: isoDateTimeSchema,
  /** The weekly times of the level, and the ones Teamna picks first (the days of the coaches who added it). */
  weeklyTimes: z.array(levelWeeklyTimeSchema),
  defaultWeekdays: z.array(z.number().int().min(0).max(6)),
  suggestions: z.array(parentSuggestionSchema),
  /** Sessions this name came to before it had a parent ("Someone else came", migration 0038). */
  visits: z.array(z.object({ sessionId: uuidSchema, startsAt: isoDateTimeSchema, groupName: z.string() })).optional(),
  /** The coach "Ask on WhatsApp" asks about this name (migration 0046): a coach of the name on the team. Null when none. */
  askCoach: z.object({ userId: uuidSchema, name: z.string() }).nullable().optional(),
  /** A coach says a swimmer who already has a parent is theirs too: the owner only adds days. */
  participant: z
    .object({ id: uuidSchema, familyId: uuidSchema, weekdays: z.array(z.number().int().min(0).max(6)) })
    .nullable(),
});
export type OwnerWaiting = z.infer<typeof ownerWaitingSchema>;

/** A parent the owner can link a name to. */
export const ownerParentSchema = z.object({
  familyId: uuidSchema,
  name: z.string(),
  phoneE164: z.string().nullable(),
  childNames: z.array(z.string()),
  /** The WhatsApp sync sorted this person as a parent and no swimmer is linked yet. */
  fromSync: z.boolean(),
});
export type OwnerParent = z.infer<typeof ownerParentSchema>;

/** A name the owner linked (migration 0041): the Swimmers panel shows it, with Unlink. */
export const ownerLinkedSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  groupId: uuidSchema,
  groupName: z.string(),
  coachNames: z.array(z.string()),
  participantId: uuidSchema.nullable(),
  familyId: uuidSchema.nullable(),
  parentName: z.string().nullable(),
  parentPhoneE164: z.string().nullable(),
  /** Where the family stands with the invite: the last step invites the families with none yet. */
  parentInvite: familyInviteStateSchema.nullable(),
  linkKind: pendingLinkKindSchema.nullable(),
  linkedVia: pendingLinkedViaSchema.nullable(),
  linkedAt: isoDateTimeSchema.nullable(),
});
export type OwnerLinked = z.infer<typeof ownerLinkedSchema>;

/**
 * A person of a synced WhatsApp group (or of a pasted list) who can be a parent: the steps "Name
 * the numbers" and "Pick a parent" read these. Team members and skipped people are not here.
 */
export const groupPersonSchema = z.object({
  id: uuidSchema,
  /** The name the owner typed, else the WhatsApp name cleaned by AI, else the WhatsApp name, else null. */
  name: z.string().nullable(),
  whatsappName: z.string().nullable(),
  /** The WhatsApp name the AI cleaned into `name` ("i$mAil" for Ismail). Null when the name is as WhatsApp shows it. */
  cleanedFrom: z.string().nullable(),
  phoneE164: z.string(),
  /** `named`: a usable name. `number_only`: WhatsApp gave no name. `unclear`: a name with no real letters ("MB", emoji). */
  nameKind: z.enum(["named", "number_only", "unclear"]),
  nameEdited: z.boolean(),
  /** Sorted as a parent already (a family on the Families page). */
  isParent: z.boolean(),
  fromGroup: z.boolean(),
  leftGroup: z.boolean(),
  /** The profile photo of a sorted person; null while unsorted (never a WhatsApp photo). Owner and admins only. */
  photoUrl: z.string().nullable(),
});
export type GroupPerson = z.infer<typeof groupPersonSchema>;

/** A match Teamna found: waiting swimmers (siblings together) and the person who is likely their parent. */
export const parentMatchSchema = z.object({
  id: uuidSchema,
  /** The waiting swimmers of the match that still wait. */
  pendingIds: z.array(uuidSchema).min(1),
  contactId: uuidSchema.nullable(),
  confidence: parentMatchConfidenceSchema,
  reason: z.string(),
  source: parentMatchSourceSchema,
});
export type ParentMatch = z.infer<typeof parentMatchSchema>;

/**
 * An answer to "Ask the parents" (migration 0045): a person of the group says these names are their
 * children. `pendingIds` are the names of the answer that still wait (a linked or removed name
 * drops out); `names` are all the names of the answer, for the row text.
 */
export const parentClaimSchema = z.object({
  id: uuidSchema,
  pendingIds: z.array(uuidSchema),
  /** All the names the parent picked, with the name the coach typed, for the row text. */
  names: z.array(z.string()),
  contactId: uuidSchema,
  /** The name the person typed for themselves. */
  name: z.string(),
  relation: parentClaimRelationSchema.nullable(),
  /** What the parent wrote to the business, if anything. */
  note: z.string().nullable(),
  phoneE164: z.string(),
  /** The name WhatsApp shows for this number, when there is one. */
  whatsappName: z.string().nullable(),
  source: parentClaimSourceSchema,
  coachName: z.string().nullable(),
  status: parentClaimStatusSchema,
  createdAt: isoDateTimeSchema,
});
export type ParentClaim = z.infer<typeof parentClaimSchema>;

/** A question to a coach about the parent of waiting names (migration 0046). */
export const parentAskSchema = z.object({
  id: uuidSchema,
  pendingIds: z.array(uuidSchema),
  coachUserId: uuidSchema,
  coachName: z.string(),
  status: parentAskStatusSchema,
  whatsappStatus: staffRequestWhatsappStatusSchema,
  createdAt: isoDateTimeSchema,
});
export type ParentAsk = z.infer<typeof parentAskSchema>;

/** `GET /business/businesses/:id/new-participants`. */
export const ownerNewParticipantsResponseSchema = z.object({
  waiting: z.array(ownerWaitingSchema),
  parents: z.array(ownerParentSchema),
  /** Names linked in the last 60 days, newest first. */
  linked: z.array(ownerLinkedSchema),
  people: z.array(groupPersonSchema),
  /** People of the groups who are on the team (a coach, an admin): never a parent. Step 1 shows them as left out. */
  team: z.array(groupPersonSchema),
  matches: z.array(parentMatchSchema),
  /** Answers to "Ask the parents": open ones, and the ones decided in the last 30 days. */
  claims: z.array(parentClaimSchema),
  /** Questions to coaches about a parent, the latest per name, from the last 30 days. */
  coachAsks: z.array(parentAskSchema),
  /** The message for the parents' group: the link, and when the owner last copied it or a reminder. */
  askParents: z.object({
    findUrl: z.string(),
    postedAt: isoDateTimeSchema.nullable(),
    remindedAt: isoDateTimeSchema.nullable(),
  }),
  /** The bar on this page and on Home: names linked and names that wait, since the oldest waiting name came in. */
  progress: z.object({ linked: z.number().int(), waiting: z.number().int() }),
  matcher: z.object({
    /** `ai` when a language model is set up, else `rule` (family names only). */
    source: parentMatchSourceSchema,
    lastRunAt: isoDateTimeSchema.nullable(),
    /** Names of the group the AI did not read yet (0 without a model). The page asks for them once. */
    namesToClean: z.number().int(),
  }),
});
export type OwnerNewParticipantsResponse = z.infer<typeof ownerNewParticipantsResponseSchema>;

/** `POST .../new-participants/:id/link`: where the name goes, and the days. */
export const linkNewParticipantRequestSchema = z.object({
  /** At least one day when the level has weekly times. A level with none yet takes an empty list. */
  weekdays: z.array(z.number().int().min(0).max(6)).max(7),
  to: z.discriminatedUnion("kind", [
    /** `withoutName`: as on `contact`. A family listed by its contact with no account yet gets one with no name. */
    z.object({ kind: z.literal("family"), familyId: uuidSchema, withoutName: z.boolean().optional() }),
    /**
     * `invite` is ignored: a link never sends an invite (link-parents-sync). Older apps still send it.
     * The parent name can be empty: the parent shows as the number until they add their own name.
     */
    z.object({ kind: z.literal("new_family"), parentName: z.string().trim().max(120), phone: phoneInputSchema.min(1), invite: z.boolean().optional() }),
    z.object({ kind: z.literal("self"), phone: phoneInputSchema.min(1), invite: z.boolean().optional() }),
    z.object({ kind: z.literal("participant") }),
    /** A person from the synced WhatsApp group: they become a parent (a family), with this child. */
    /**
     * `withoutName`: "Link without a name". A new account gets no name, not the WhatsApp name of the
     * contact: the parent shows as the number until they add their own name.
     */
    z.object({ kind: z.literal("contact"), contactId: uuidSchema, withoutName: z.boolean().optional() }),
  ]),
  /** Who found the parent. A link from a match also names the match. */
  /** `parent` and `coach` come from an answer (`.../claims/:id/link`), never from this route. */
  via: z.enum(["owner", "match"]).optional(),
  matchId: uuidSchema.optional(),
});
export type LinkNewParticipantRequest = z.infer<typeof linkNewParticipantRequestSchema>;

export const linkNewParticipantResponseSchema = z.object({
  id: uuidSchema,
  participantId: uuidSchema,
  familyId: uuidSchema,
  invited: z.boolean(),
});
export type LinkNewParticipantResponse = z.infer<typeof linkNewParticipantResponseSchema>;

/** `POST .../new-participants/matches`: Teamna reads the names again and finds parents. */
export const runParentMatchResponseSchema = z.object({
  matches: z.array(parentMatchSchema),
  source: parentMatchSourceSchema,
  /** The model was set up but failed, so the family-name rule ran instead. */
  fellBack: z.boolean(),
});
export type RunParentMatchResponse = z.infer<typeof runParentMatchResponseSchema>;

/** `POST .../new-participants/:id/unlink`: the name waits again. */
export const unlinkNewParticipantResponseSchema = z.object({ id: uuidSchema });
export type UnlinkNewParticipantResponse = z.infer<typeof unlinkNewParticipantResponseSchema>;

/** `POST .../new-participants/clean-names`: the AI read the names of the group it did not read yet. */
export const cleanGroupNamesResponseSchema = z.object({ asked: z.number().int(), cleaned: z.number().int(), fellBack: z.boolean() });
export type CleanGroupNamesResponse = z.infer<typeof cleanGroupNamesResponseSchema>;

/**
 * Names from the owner's phone contacts file (.vcf). The browser reads the file and matches the
 * numbers to the people of the group, so only the matches come here: never the whole file.
 */
export const nameGroupPeopleRequestSchema = z.object({
  names: z
    .array(z.object({ id: uuidSchema, name: z.string().trim().min(2).max(120) }))
    .min(1)
    .max(1000),
});
export type NameGroupPeopleRequest = z.infer<typeof nameGroupPeopleRequestSchema>;
/** `named`: the people who took a name, `namedIds` who they are. `skipped`: a name came for them but they have one already or are not on the page any more. */
export const nameGroupPeopleResponseSchema = z.object({ named: z.number().int(), namedIds: z.array(uuidSchema), skipped: z.number().int() });
export type NameGroupPeopleResponse = z.infer<typeof nameGroupPeopleResponseSchema>;

/** One coach on the "Ask coaches" card. */
export const staffAskRowSchema = z.object({
  userId: uuidSchema,
  name: z.string(),
  phoneE164: z.string().nullable(),
  groupNames: z.array(z.string()),
  lastAskedAt: isoDateTimeSchema.nullable(),
  lastStatus: staffRequestWhatsappStatusSchema.nullable(),
  /** Names this coach added, and how many of them still wait. */
  added: z.number().int(),
  waiting: z.number().int(),
});
export type StaffAskRow = z.infer<typeof staffAskRowSchema>;

/** `GET .../staff-requests`: the card on the Team page. */
export const staffAskResponseSchema = z.object({
  canAdd: z.boolean(),
  /** The message each coach gets, with `{name}` for the coach's first name. */
  messagePreview: z.string(),
  /** False when WhatsApp is not connected: Teamna records the ask and sends the push only. */
  whatsappReady: z.boolean(),
  staff: z.array(staffAskRowSchema),
});
export type StaffAskResponse = z.infer<typeof staffAskResponseSchema>;

/** `POST .../staff-requests`: ask these coaches (all coaches when empty). */
export const sendStaffAskRequestSchema = z.object({ staffUserIds: z.array(uuidSchema).max(200).optional() });
export type SendStaffAskRequest = z.infer<typeof sendStaffAskRequestSchema>;

export const sendStaffAskResponseSchema = z.object({
  results: z.array(z.object({ userId: uuidSchema, name: z.string(), status: staffRequestWhatsappStatusSchema })),
});
export type SendStaffAskResponse = z.infer<typeof sendStaffAskResponseSchema>;

// --- someone else came (slice 2, migration 0038) ----------------------------------

/** One result of the coach's search: a swimmer of the club, or a name that waits for a parent. */
export const sessionExtraCandidateSchema = z.object({
  kind: z.enum(["participant", "waiting"]),
  id: uuidSchema,
  name: z.string(),
  /** "Learn to swim, Tue and Thu with Hala". Level and coaches only, nothing about the family. */
  where: z.string(),
});
export type SessionExtraCandidate = z.infer<typeof sessionExtraCandidateSchema>;

/** `GET .../sessions/:id/extras/search?q=`. */
export const sessionExtraSearchResponseSchema = z.object({
  items: z.array(sessionExtraCandidateSchema),
  /** Names like the typed one that are in this session already. */
  inSession: z.array(z.string()),
});
export type SessionExtraSearchResponse = z.infer<typeof sessionExtraSearchResponseSchema>;

/** `GET .../sessions/:id/extras/not-booked`: the swimmers and waiting names of the session's level with no booking in it. */
export const sessionNotBookedResponseSchema = z.object({
  /** Those who usually come on the session's weekday first, then by name. */
  items: z.array(
    sessionExtraCandidateSchema.extend({
      usualDay: z.boolean(),
      /** The family's sessions left for this class, for the bar on the register. Null for a name that waits for a parent. Optional for older API builds. */
      sessionsLeft: sessionsLeftSchema.nullable().optional(),
    }),
  ),
  /** The session's weekday in the business's clock, 0 is Sunday. */
  weekday: z.number().int().min(0).max(6),
  /** An open class (migration 0062): no swimmer belongs to it, so the list is empty. Search the club instead. */
  openToAll: z.boolean(),
});
export type SessionNotBookedResponse = z.infer<typeof sessionNotBookedResponseSchema>;
export type SessionNotBookedItem = SessionNotBookedResponse["items"][number];

/**
 * Where the coach found the name: the level's "Did not book" list, the search of the club, or the
 * attendance register, which lists the level's swimmers who did not book (`register`). For analytics only.
 */
export const sessionExtraFromSchema = z.enum(["not_booked", "search", "register"]);

/** `POST .../sessions/:id/extras`: a swimmer of the club, a waiting name, or a new name. */
export const addSessionExtraRequestSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("participant"), participantId: uuidSchema, from: sessionExtraFromSchema.optional() }),
  z.object({ kind: z.literal("waiting"), pendingParticipantId: uuidSchema, from: sessionExtraFromSchema.optional() }),
  z.object({
    kind: z.literal("new"),
    name: personNameSchema,
    noteParentName: personNameSchema.nullable().optional(),
    notePhone: phoneInputSchema.nullable().optional(),
  }),
]);
export type AddSessionExtraRequest = z.infer<typeof addSessionExtraRequestSchema>;

export const addSessionExtraResponseSchema = z.object({
  extraId: uuidSchema,
  name: z.string(),
  /** Came made an owed session: no package had a session left (2026-10-29). Optional for older API builds. */
  owed: z.boolean().optional(),
});
export type AddSessionExtraResponse = z.infer<typeof addSessionExtraResponseSchema>;

/**
 * Came during the pause (2026-10-29): Came took a session from a paused package, and the pause stays
 * until the owner answers. `GET .../came-during-pause`.
 */
export const cameDuringPauseItemSchema = z.object({
  /** The visit (`session_extras.id`). */
  id: uuidSchema,
  participantId: uuidSchema,
  name: z.string(),
  groupName: z.string(),
  startsAt: isoDateTimeSchema,
  purchaseId: uuidSchema,
  freezeId: uuidSchema,
  /** The last paused day. */
  pausedUntil: z.string(),
  markedAt: isoDateTimeSchema,
});
export const cameDuringPauseResponseSchema = z.object({ items: z.array(cameDuringPauseItemSchema) });
export type CameDuringPauseResponse = z.infer<typeof cameDuringPauseResponseSchema>;
export type CameDuringPauseItem = CameDuringPauseResponse["items"][number];

/** `POST .../came-during-pause/:id`: End the pause (`end: true`), or Keep the pause. */
export const answerCameDuringPauseRequestSchema = z.object({ end: z.boolean() });
export type AnswerCameDuringPauseRequest = z.infer<typeof answerCameDuringPauseRequestSchema>;

/** A visit the owner decides: a swimmer who came to a session and was not booked. */
export const ownerSessionExtraSchema = z.object({
  id: uuidSchema,
  sessionId: uuidSchema,
  startsAt: isoDateTimeSchema,
  groupName: z.string(),
  /** The coach who added the swimmer. */
  staffName: z.string().nullable(),
  name: z.string(),
  /** `waiting`: the name has no parent yet; link it first (New swimmers). */
  kind: z.enum(["participant", "waiting"]),
  participantId: uuidSchema.nullable(),
  pendingParticipantId: uuidSchema.nullable(),
  familyName: z.string().nullable(),
  /** "Learn to swim, Tue and Thu with Hala", for a swimmer of the club. */
  where: z.string().nullable(),
  /** The family has a package with a session left that covers this level. */
  canTake: z.boolean(),
  /** The session's weekday, 0 is Sunday, and whether the swimmer already has that day. */
  weekday: z.number().int().min(0).max(6),
  onWeekday: z.boolean(),
  markedAt: isoDateTimeSchema,
});
export type OwnerSessionExtra = z.infer<typeof ownerSessionExtraSchema>;

/** `GET .../session-extras`: the visits to decide. A visit nobody decides for 7 days drops off. */
export const ownerSessionExtrasResponseSchema = z.object({ items: z.array(ownerSessionExtraSchema) });
export type OwnerSessionExtrasResponse = z.infer<typeof ownerSessionExtrasResponseSchema>;

/** `POST .../session-extras/:id/decide`. `addWeekday` puts the session's weekday in the swimmer's days. */
export const decideSessionExtraRequestSchema = z.object({
  money: z.enum(["take", "free"]),
  addWeekday: z.boolean().optional(),
});
export type DecideSessionExtraRequest = z.infer<typeof decideSessionExtraRequestSchema>;

/** `POST .../new-participants/ask-parents`: the owner copied the group message or the reminder. */
export const askParentsRequestSchema = z.object({ kind: z.enum(["post", "reminder"]) });
export const askParentsResponseSchema = z.object({ findUrl: z.string() });

/** `POST .../new-participants/claims/:id/link`: link the names of an answer to the person who sent it. */
export const linkParentClaimRequestSchema = z.object({
  /** The days of each name, by pending id (the coach's days, else every day of the level). */
  weekdays: z.record(z.string(), z.array(z.number().int().min(0).max(6)).max(7)),
});
export const linkParentClaimResponseSchema = z.object({
  /** Names linked by this answer. With `secondAdult`, the names that joined that family. */
  linked: z.number().int(),
  linkedNames: z.array(z.string()),
  /** Names of the answer that have a parent in another family now: not linked. */
  skippedNames: z.array(z.string()),
  familyId: uuidSchema.nullable(),
  /** The person became a second adult of the family of a name that had a parent (the other parent answered first). */
  secondAdult: z.boolean(),
});

// ---------------------------------------------------------------------------------------------
// The find page: `/find/:code`, the link in the group message. A parent signs in with the number
// they use in the group, finds their child by first name, and sends their name.
// ---------------------------------------------------------------------------------------------

/**
 * `GET /v1/find/:code`, for a signed-in person: the business, their own linked children there, and
 * whether their number may search and answer (a person of the group, not on the team).
 */
export const findPageSchema = z.object({
  businessName: z.string(),
  signedIn: z.boolean(),
  /** The signed-in number is a person of the business's WhatsApp group (and not on the team). */
  inGroup: z.boolean(),
  /** The name Teamna knows for this number, to fill "Your name". */
  knownName: z.string().nullable(),
  /** Names this number sent already that wait for the owner. */
  sent: z.array(z.string()),
  /** This person's own children at the business, already linked to them: nothing to find for these. */
  linked: z.array(z.object({ name: z.string(), level: z.string().nullable() })),
});
export type FindPage = z.infer<typeof findPageSchema>;

/**
 * `GET /v1/find/:code/children?q=`: waiting names that match `q` by the name search rule
 * (`name-search.ts`), with one typed word on the first name. 5 at most, best first, full name.
 */
export const findChildSchema = z.object({ id: uuidSchema, name: z.string(), level: z.string() });
export type FindChild = z.infer<typeof findChildSchema>;
export const findChildrenResponseSchema = z.object({
  items: z.array(findChildSchema),
  /** No waiting name fits, but a child of another family has this first name: "already linked to a parent". */
  linkedElsewhere: z.boolean(),
  /** No waiting name fits, but a child linked to this person has this first name (shown above). */
  linkedToYou: z.boolean(),
});

export const FIND_NOTE_MAX = 300;
/** Children in one answer of the find page. The page stops adding at this number. */
export const FIND_CHILDREN_MAX = 6;
/** `POST /v1/find/:code/claims`: these are my children, my name, and a note to the business if I want. */
export const findClaimRequestSchema = z.object({
  pendingIds: z.array(uuidSchema).min(1).max(FIND_CHILDREN_MAX),
  name: z.string().trim().min(2).max(80),
  relation: parentClaimRelationSchema,
  note: z.string().trim().max(FIND_NOTE_MAX).optional(),
});
export type FindClaimRequest = z.input<typeof findClaimRequestSchema>;
export const findClaimResponseSchema = z.object({ id: uuidSchema });

// ---------------------------------------------------------------------------------------------
// Ask the coach on WhatsApp (migration 0046)
// ---------------------------------------------------------------------------------------------

/** `POST .../new-participants/coach-asks`: ask the coach of these names who the parent is. */
export const askCoachRequestSchema = z.object({
  pendingIds: z.array(uuidSchema).min(1).max(6),
  /** The coach the button names (`askCoach` of the first name): the server asks this coach or refuses. */
  coachUserId: uuidSchema,
});
export const askCoachResponseSchema = z.object({ id: uuidSchema, coachName: z.string(), whatsappStatus: staffRequestWhatsappStatusSchema });

/** `GET .../staff/parent-wanted`: the coach's open questions, and the people of the group by name only. */
export const staffParentWantedResponseSchema = z.object({
  asks: z.array(z.object({ id: uuidSchema, names: z.array(z.string()), level: z.string(), askedAt: isoDateTimeSchema })),
  /** People of the group with a name. Never a number, and never a person WhatsApp shows only as a number. */
  people: z.array(z.object({ id: uuidSchema, name: z.string() })),
});
export type StaffParentWantedResponse = z.infer<typeof staffParentWantedResponseSchema>;

/** `POST .../staff/parent-wanted/:id/answer`. */
export const answerParentWantedRequestSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("pick"), contactId: uuidSchema }),
  z.object({ kind: z.literal("type"), name: z.string().trim().min(2).max(80), phone: z.string().trim().min(6).max(30) }),
  z.object({ kind: z.literal("dont_know") }),
]);
export type AnswerParentWantedRequest = z.infer<typeof answerParentWantedRequestSchema>;
